/**
 * Norce Checkout API Service
 * Handles all API calls to the Norce Checkout backend endpoints
 */

import useApiFetch from '~/composables/useApiFetch';
import type {
  InitializeResponse,
  AdyenTransactionRequest,
  AdyenTransactionResponse,
  AdyenDetailsRequest,
  AdyenDetailsResponse,
  AdyenRefreshResponse,
  PaymentStatusResponse,
  NonPspSelectMethodResponse,
  NonPspCompleteResponse,
  UpdateAddressRequest,
  AvailableAdaptersResponse,
  SwitchAdapterResponse,
  PaymentAdapter,
  KlarnaConfirmationResponse,
  WalleyConfirmationResponse,
} from './types';

export default function useNorceCheckoutApi() {
  const { apiGet, apiPost, apiPut, lastError, lastErrorCode } = useApiFetch();

  /**
   * Initialize checkout session
   * Creates a Norce order and Adyen payment session
   */
  const initializeCheckout = async (basketUuid: string): Promise<InitializeResponse | null> => {
    return await apiPost<InitializeResponse>('/norce-checkout/initialize', {
      basketUuid,
    }, null, false);
  };

  /**
   * Process Adyen payment transaction
   * Submits the payment data to Adyen via Norce
   */
  const processAdyenTransaction = async (
    orderId: string,
    paymentId: string,
    transactionData: AdyenTransactionRequest['transactionData']
  ): Promise<AdyenTransactionResponse | null> => {
    return await apiPost<AdyenTransactionResponse>('/norce-checkout/adyen/transaction', {
      orderId,
      paymentId,
      transactionData,
    }, null, false);
  };

  /**
   * Complete Adyen payment details (for 3DS authentication, Swish, etc.)
   * Handles additional authentication steps required by Adyen
   */
  const completeAdyenDetails = async (
    orderId: string,
    paymentId: string,
    details: Record<string, unknown>,
    paymentData?: string
  ): Promise<AdyenDetailsResponse | null> => {
    return await apiPost<AdyenDetailsResponse>('/norce-checkout/adyen/details', {
      orderId,
      paymentId,
      details,
      ...(paymentData && { paymentData }),
    }, null, false);
  };

  /**
   * Refresh Adyen payment session
   * Gets updated payment methods after cart changes
   */
  const refreshAdyenPayment = async (
    orderId: string,
    paymentId: string
  ): Promise<AdyenRefreshResponse | null> => {
    return await apiGet<AdyenRefreshResponse>(
      `/norce-checkout/adyen/${orderId}/payments/${paymentId}`,
      null,
      false
    );
  };

  /**
   * Get payment status
   * Checks current payment adapter and gets updated payment data
   * Used for detecting adapter switches (e.g., adyen <-> nonpsp)
   */
  const getPaymentStatus = async (
    basketUuid: string
  ): Promise<PaymentStatusResponse | null> => {
    return await apiGet<PaymentStatusResponse>(
      `/norce-checkout/status/${basketUuid}`,
      null,
      false
    );
  };

  // ============================================
  // Non-PSP Payment Methods (for free orders)
  // ============================================

  /**
   * Select a Non-PSP payment method
   * Used for free orders where no actual payment processing is needed
   */
  const selectNonPspPaymentMethod = async (
    orderId: string,
    paymentMethod: string
  ): Promise<NonPspSelectMethodResponse | null> => {
    return await apiPut<NonPspSelectMethodResponse>(
      `/norce-checkout/nonpsp/${orderId}/payment-method`,
      { paymentMethod }
    );
  };

  /**
   * Complete a Non-PSP payment
   * Finalizes the order for free orders
   */
  const completeNonPspPayment = async (
    orderId: string,
    paymentId: string
  ): Promise<NonPspCompleteResponse | null> => {
    return await apiPost<NonPspCompleteResponse>(
      `/norce-checkout/nonpsp/${orderId}/payments/${paymentId}/complete`,
      {},
      null,
      false
    );
  };

  // ============================================
  // Payment Adapter Management
  // ============================================

  /**
   * Get available payment adapters
   * Returns the default adapter and list of available adapters for the current market
   */
  const getAvailableAdapters = async (): Promise<AvailableAdaptersResponse | null> => {
    return await apiGet<AvailableAdaptersResponse>(
      '/norce-checkout/adapters',
      null,
      false
    );
  };

  /**
   * Switch payment adapter
   * Changes the payment adapter for the current checkout session
   * Returns updated payment data for the new adapter
   */
  const switchAdapter = async (
    basketUuid: string,
    adapter: PaymentAdapter
  ): Promise<SwitchAdapterResponse | null> => {
    return await apiPut<SwitchAdapterResponse>(
      `/norce-checkout/adapter/${basketUuid}`,
      { adapter }
    );
  };

  // ============================================
  // Customer Address Updates (B2C only)
  // ============================================

  /**
   * Update billing address via Norce Checkout
   * Used for B2C checkout when Norce Checkout address updates are enabled
   */
  const updateBillingAddress = async (
    request: UpdateAddressRequest
  ): Promise<PaymentStatusResponse | null> => {
    return await apiPut<PaymentStatusResponse>(
      '/norce-checkout/customer/billing',
      request
    );
  };

  /**
   * Update shipping address via Norce Checkout
   * Used for B2C checkout when Norce Checkout address updates are enabled
   */
  const updateShippingAddress = async (
    request: UpdateAddressRequest
  ): Promise<PaymentStatusResponse | null> => {
    return await apiPut<PaymentStatusResponse>(
      '/norce-checkout/customer/shipping',
      request
    );
  };

  // ============================================
  // Klarna Confirmation
  // ============================================

  /**
   * Get Klarna confirmation/receipt snippet
   * Called on success page to display Klarna confirmation widget
   */
  const getKlarnaConfirmation = async (
    orderId: string,
    paymentId: string
  ): Promise<KlarnaConfirmationResponse | null> => {
    return await apiGet<KlarnaConfirmationResponse>(
      `/norce-checkout/klarna/${orderId}/payments/${paymentId}`,
      null,
      false
    );
  };

  // ============================================
  // Walley Confirmation
  // ============================================

  /**
   * Get Walley confirmation/receipt snippet
   * Called on success page to display Walley confirmation widget
   */
  const getWalleyConfirmation = async (
    orderId: string,
    paymentId: string
  ): Promise<WalleyConfirmationResponse | null> => {
    return await apiGet<WalleyConfirmationResponse>(
      `/norce-checkout/walley/${orderId}/payments/${paymentId}`,
      null,
      false
    );
  };

  return {
    initializeCheckout,
    // Adyen-specific methods
    processAdyenTransaction,
    completeAdyenDetails,
    refreshAdyenPayment,
    getPaymentStatus,
    // Payment adapter management
    getAvailableAdapters,
    switchAdapter,
    // Non-PSP methods
    selectNonPspPaymentMethod,
    completeNonPspPayment,
    // Customer address updates (B2C)
    updateBillingAddress,
    updateShippingAddress,
    // Klarna
    getKlarnaConfirmation,
    // Walley
    getWalleyConfirmation,
    lastError,
    lastErrorCode,
  };
}
